import { useFormik } from 'formik'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

import { useCreateAccountMutation, useUpdateAccountMutation } from 'api/rtkQueryApi/platform/accountsApi'
import { showErrorToast } from 'app/appSlice'

import { ApiError, apiGetBusinessType } from '../../api/adcritterApi'
import { Account, BusinessType } from '../../api/interfaces'
import { RootState } from '../../app/rootReducer'
import { AccountFormValues } from './AccountFormValues'

export interface UseAccountFormProps {
  isNewAccount: boolean
  onSubmit?: (account: Account) => void
  accountSchema: Yup.ObjectSchema<any>
}

export const useAccountForm = ({ isNewAccount, onSubmit, accountSchema }: UseAccountFormProps) => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [selectedBusinessType, setSelectedBusinessType] = useState<BusinessType | null>(null)
  const [createAccountMutation] = useCreateAccountMutation()
  const [updateAccountMutation] = useUpdateAccountMutation()
  const dispatch = useDispatch()

  const defaultFormValues: AccountFormValues = {
    businessTypeId: '',
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'usa',
    website: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: ''
  }

  const [formValues, setFormValues] = useState<AccountFormValues>(defaultFormValues)

  useEffect(() => {
    if (!isNewAccount && currentAccount != null) {
      setFormValues({
        businessTypeId: currentAccount.businessTypeId || '',
        name: currentAccount.name,
        streetAddress: currentAccount.streetAddress || '',
        state: currentAccount.state || '',
        city: currentAccount.city || '',
        zip: currentAccount.zip || '',
        website: currentAccount.website || '',
        country: 'usa',
        contactFirstName: currentAccount.contactFirstName || '',
        contactLastName: currentAccount.contactLastName || '',
        contactEmail: currentAccount.contactEmail || '',
        contactPhone: currentAccount.contactPhone || ''
      })

      if (currentAccount.businessTypeId) {
        apiGetBusinessType(currentAccount.businessTypeId).then(result => {
          setSelectedBusinessType(result.data)
        })
      }
    } else {
      setFormValues(defaultFormValues)
      setSelectedBusinessType(null)
    }
  }, [currentAccount, isNewAccount])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formValues,
    validationSchema: accountSchema,
    onSubmit: (values: AccountFormValues) => {
      if (isNewAccount) {
        createAccountMutation(values)
          .unwrap()
          .then(response => {
            formik.setSubmitting(false)
            formik.setStatus(null)
            onSubmit?.(response)
          })
          .catch(error => {
            formik.setSubmitting(false)
            error.errors.forEach(function (e: ApiError) {
              formik.setFieldError(e.name, e.message)
            })
          })
      } else {
        updateAccountMutation({ accountId: currentAccount!.id, request: values })
          .unwrap()
          .then(response => {
            formik.setSubmitting(false)
            formik.setStatus(null)
            onSubmit?.(response)
          })
          .catch(error => {
            formik.setSubmitting(false)
            error.errors.forEach(function (e: ApiError) {
              formik.setFieldError(e.name, e.message)
            })
          })
      }
    }
  })

  const selectBusinessType = (selected: BusinessType | null) => {
    setSelectedBusinessType(selected)
    if (!!selected) {
      formik.setFieldValue('businessTypeId', selected.id)
    }
  }

  return { formik, selectedBusinessType, selectBusinessType }
}
